import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { UploadProgress as UploadProgressType } from "../../types/upload";
import { FaCloudArrowUp, FaPlay } from "react-icons/fa6";
import { UploadProgress } from "./UploadProgress";
import {
  useGetPresignedUrlMutation,
  useGetPresignedUrlForChatMutation,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import {
  AWS_CLOUD_FRONT_URL,
  MAX_VIDEO_SIZE_BYTES,
  MAX_VIDEO_SIZE_MB,
  FILE_TYPE_ID,
  VIDEO_STATUS,
} from "../../lib/constants";
import { FaTimes } from "react-icons/fa";
import VideoPreviewModal from "./VideoPreviewModal";
import { RiDeleteBinLine } from "react-icons/ri";
import { uploadToS3WithPost } from "../../lib/utils";
import { useSearchParams } from "react-router-dom";
import { HLSPlayer } from "./HLSPlayer";

export interface VideoUploaderProps {
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setVideoDetails: React.Dispatch<
    React.SetStateAction<{
      title: string;
      // description: string;
      filePath: string;
      deletedVideoFileObj?: {
        filePath: string;
      };
    }>
  >;
  statusId?: number;
  isForEditVideo: boolean;
  filePath: string;
  imageAllowed?: boolean;
  urlUseS3?: boolean;
  isForChat?: boolean;
}

export const VideoUploader: React.FC<VideoUploaderProps> = ({
  statusId,
  setIsDisabled,
  setVideoDetails,
  filePath = "",
  imageAllowed = false,
  urlUseS3 = false,
  isForChat = false,
}) => {
  const [uploads, setUploads] = useState<
    Map<
      string,
      { file: File; progress: UploadProgressType; controller: AbortController }
    >
  >(new Map());

  const [deletedVideoPath, setDeletedVideoPath] = useState("");

  const [getPresignedUrl, { isLoading: isGettingPresignedUrl }] =
    useGetPresignedUrlMutation();
  const [getPresignedUrlForChat, { isLoading: isGettingPresignedUrlForChat }] =
    useGetPresignedUrlForChatMutation();
  const { toast } = useToast();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleDelete = () => {
    if (statusId === VIDEO_STATUS.VIDEO_IN_PROCESS) {
      toast({
        title: "Error",
        description: "You cannot delete a video in process. Please wait.",
      });
      return;
    }
    setDeletedVideoPath(filePath);
    setVideoDetails((prev) => ({
      ...prev,
      filePath: "",
      deletedVideoFileObj: {
        filePath: filePath,
      },
    }));
    setUploads(new Map());
  };

  const handleUpload = async (file: File) => {
    const isImage = file.type.startsWith("image/");
    if (filePath) {
      toast({
        title: "Error",
        description:
          "Please delete the existing video before uploading a new one.",
      });
      return;
    }

    const existingFailedUpload = Array.from(uploads.entries()).find(
      ([, { progress }]) => progress.status === "error"
    );

    if (existingFailedUpload) {
      const [failedUploadId] = existingFailedUpload;

      // Remove the failed upload
      setUploads((prev) => {
        const newMap = new Map(prev);
        newMap.delete(failedUploadId);
        return newMap;
      });
    }

    if (uploads.size > 0) {
      toast({
        title: "Error",
        description: "You can only upload one video at a time.",
      });
      return;
    }
    const uploadId = Math.random().toString(36).substring(7);
    const controller = new AbortController();

    try {
      // Initialize upload progress
      setUploads((prev) =>
        new Map(prev).set(uploadId, {
          file,
          progress: { progress: 0, status: "uploading" },
          controller,
        })
      );

      let correctedFileName = file.name;
      const fileNameParts = file.name.split(".");
      const baseName = fileNameParts.slice(0, -1).join(".");
      const extension = fileNameParts[fileNameParts.length - 1];
      correctedFileName = `${baseName}-${uploadId}.${extension}`;

      const presignedUrl = await (isForChat
        ? getPresignedUrlForChat
        : getPresignedUrl)({
        body: {
          fileName: correctedFileName,
          fileType: file.type,
          index: 0,
          isEdit: false,
        },
      }).unwrap();

      if (!presignedUrl.url) {
        toast({
          title: "Error",
          description:
            "Failed to get pre-signed URL. Please try again after some time!",
        });
        return;
      }

      // Upload to S3
      setIsDisabled(true);
      await uploadToS3WithPost(
        file,
        presignedUrl.url,
        presignedUrl.fields,
        (progress) => {
          setUploads((prev) => {
            const newMap = new Map(prev);
            const upload = newMap.get(uploadId);
            if (upload) {
              newMap.set(uploadId, {
                ...upload,
                progress: { ...upload.progress, progress },
              });
            }
            return newMap;
          });
        },
        controller.signal
      );

      // Update status to completed
      setUploads((prev) => {
        const newMap = new Map(prev);
        const upload = newMap.get(uploadId);
        if (upload) {
          newMap.set(uploadId, {
            ...upload,
            progress: { progress: 100, status: "completed" },
          });
        }
        return newMap;
      });

      console.log("presignedUrl.url", presignedUrl.url);
      const filePath = isImage
        ? `${urlUseS3 ? presignedUrl.url : `${AWS_CLOUD_FRONT_URL}/`}${
            presignedUrl.fields.key
          }`
        : `${urlUseS3 ? presignedUrl.url : `${AWS_CLOUD_FRONT_URL}/`}videos/${
            presignedUrl.fields.key.split("/")[1]
          }`;
      setVideoDetails((prev) => ({
        ...prev,
        filePath: filePath,
        deletedVideoFileObj: prev.deletedVideoFileObj,
      }));
      setIsDisabled(false);
    } catch (error: any) {
      setIsDisabled(false);
      if (error.message === "Upload aborted") {
        toast({
          title: "Upload Cancelled",
          description: "You have cancelled the video upload.",
        });
      } else {
        toast({
          title: "Error",
          description: `${
            error?.data?.message ||
            "Failed to upload video. Please try again after some time!"
          }`,
        });
      }
      setUploads((prev) => {
        const newMap = new Map(prev);
        const upload = newMap.get(uploadId);
        if (upload) {
          newMap.set(uploadId, {
            ...upload,
            progress: {
              progress: 0,
              status: "error",
              error: "Upload failed",
            },
          });
        }
        return newMap;
      });
    }
  };

  const handleCancel = (uploadId: string) => {
    const upload = uploads.get(uploadId);
    if (upload) {
      upload.controller.abort();
      setUploads((prev) => {
        const newMap = new Map(prev);
        newMap.delete(uploadId);
        return newMap;
      });
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any) => {
      if (filePath) {
        toast({
          title: "Error",
          description:
            "Please delete the existing video before uploading a new one.",
        });
        return;
      }
      const existingFailedUpload = Array.from(uploads.entries()).find(
        ([, { progress }]) => progress.status === "error"
      );

      if (existingFailedUpload) {
        const [failedUploadId] = existingFailedUpload;

        // Remove the failed upload
        setUploads((prev) => {
          const newMap = new Map(prev);
          newMap.delete(failedUploadId);
          return newMap;
        });
      }
      if (uploads.size > 0) {
        toast({
          title: "Error",
          description: "You can only upload one video at a time.",
        });
        return;
      }
      acceptedFiles.forEach(handleUpload);
    },
    [uploads.size, filePath]
  );

  const onDropRejected = useCallback(
    (rejectedFiles: any) => {
      const existingFailedUpload = Array.from(uploads.entries()).find(
        ([, { progress }]) => progress.status === "error"
      );

      if (existingFailedUpload) {
        const [failedUploadId] = existingFailedUpload;

        // Remove the failed upload
        setUploads((prev) => {
          const newMap = new Map(prev);
          newMap.delete(failedUploadId);
          return newMap;
        });
      }
      if (filePath) {
        toast({
          title: "Error",
          description:
            "Please delete the existing video before uploading a new one.",
        });
        return;
      }
      rejectedFiles.forEach(({ file, errors }: any) => {
        if (errors.some((err: any) => err.code === "file-too-large")) {
          toast({
            title: "Error",
            description: `The file ${file.name} exceeds the maximum size of ${MAX_VIDEO_SIZE_MB} MB.`,
          });
        } else if (
          errors.some((err: any) => err.code === "file-invalid-type")
        ) {
          toast({
            title: "Error",
            description: `The file ${file.name} is not a supported format. Please upload MP4 or MOV videos.`,
          });
        } else {
          toast({
            title: "Error",
            description: `The file ${file.name} could not be uploaded. Please try again.`,
          });
        }
      });
    },
    [uploads.size]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: imageAllowed
      ? {
          "video/*": [".mp4", ".mov"],
          "image/*": [".png", ".jpg", ".jpeg"],
        }
      : {
          "video/*": [
            ".mp4",
            ".MP4",
            ".Mp4",
            ".mP4",
            ".mov",
            ".MOV",
            ".Mov",
            ".mOv",
            ".moV",
            ".MoV",
          ],
        },
    multiple: false,
    maxFiles: 1,
    maxSize: MAX_VIDEO_SIZE_BYTES,
  });

  return (
    <div className="rounded">
      <h3 className="text-lg font-semibold mb-4">
        Upload {imageAllowed ? "Video or Image" : "Video"}
        <span className="text-red-500">*</span>
      </h3>
      {(filePath && !deletedVideoPath) ||
      (uploads.size > 0 &&
        Array.from(uploads.values())[0].progress.status === "completed") ? (
        <div className="flex items-center justify-between border border-gray-300 p-4 rounded-lg mt-2">
          <div
            className="w-48 h-32 relative cursor-pointer group"
            onClick={() => setIsPreviewOpen(true)}
          >
            {filePath.includes("m3u8") ? (
              <HLSPlayer
                src={filePath}
                className="w-full h-full object-cover rounded"
              />
            ) : filePath.includes("videos") ||
              filePath.includes("video/quicktime") ||
              filePath.includes("video") ? (
              <video
                className="w-full h-full object-cover rounded"
                src={filePath}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={filePath}
                alt="Uploaded image"
                className="w-full h-full object-cover rounded"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-opacity-50 rounded-full flex items-center justify-center group-hover:bg-opacity-75 transition-all">
                {/* <FaPlay className="text-white text-xl ml-1" /> */}
              </div>
            </div>
            <button
              type="button"
              className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              <RiDeleteBinLine className="text-red-600 text-lg" />
            </button>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer  bg-grayBackgroundColor  ${
            uploads.size > 0
              ? "border-gray-300 cursor-not-allowed"
              : "hover:border-customGreenColor transition-colors"
          }`}
        >
          <input {...getInputProps()} />
          <FaCloudArrowUp className="h-12 w-12 mx-auto text-gray-400  bg-grayBackgroundColor " />
          {isDragActive ? (
            <p className="mt-4 text-lg text-gray-600">
              Drop your {imageAllowed ? "file" : "video"} here...
            </p>
          ) : (
            <div>
              <p className="mt-4 text-lg text-gray-600">
                Drag and drop your {imageAllowed ? "file" : "video"} here
              </p>
              <p className="mt-2 text-sm text-gray-500">
                or click to select file
              </p>
              <p className="mt-1 text-xs text-gray-400">
                {imageAllowed ? (
                  <>
                    Supports MP4, MOV, PNG, JPG and JPEG formats up to{" "}
                    {MAX_VIDEO_SIZE_MB} MB
                  </>
                ) : (
                  <>Supports MP4 and MOV formats up to {MAX_VIDEO_SIZE_MB} MB</>
                )}
              </p>
              <span className="text-sm font-medium mb-1 text-customGrayBase">
                {imageAllowed ? (
                  <>Recommended resolution: 1080x1920 (vertical)</>
                ) : (
                  <>Recommended video resolution: 1080x1920 (vertical)</>
                )}
              </span>
            </div>
          )}
        </div>
      )}
      <div className="space-y-4 mt-3">
        {Array.from(uploads.entries()).map(([id, { file, progress }]) => (
          <div key={id} className="flex items-center space-x-4 w-full relative">
            <UploadProgress
              file={file}
              progress={progress}
              imageAllowed={imageAllowed}
            />
            {progress.status === "uploading" && (
              <button
                type="button"
                className="text-red-500 hover:text-red-700 absolute top-4 right-4"
                onClick={() => handleCancel(id)}
              >
                <FaTimes />
              </button>
            )}

            {progress.status === "error" && (
              <button
                type="button"
                className="text-red-500 hover:text-red-700 absolute top-4 right-4"
                onClick={() => handleCancel(id)}
              >
                <FaTimes />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* dont remove this code it */}
      {/* <VideoPreviewModal
        videoSrc={filePath}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fileType={FILE_TYPE_ID.VIDEO}
      /> */}
    </div>
  );
};
